// @file The store for the AuthSignupApp
import type { LoginMethod } from '@@/bits/auth_helper'
import { normalizeThirdPartyLoginMethods } from '@@/bits/auth_helper'
import { getCookie } from '@@/bits/cookie'
import getCsrfToken from '@@/bits/csrf_token'
import { isEmail } from '@@/bits/email'
import { FACE_WITH_ROLLING_EYES_EMOJI, WAVING_HAND_EMOJI } from '@@/bits/emoji'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { p__, __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { clearQueryParamsWithoutReloading, getSearchParams, navigateTo, transformCurrentUrl } from '@@/bits/location'
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@@/bits/numbers'
import { UserRole } from '@@/bits/user_model'
import { EmailAddressVerification as EmailAddressVerificationApi } from '@@/dashboard/padlet_api'
import { ApiErrorCode, HttpCode, SnackbarNotificationType } from '@@/enums'
import { OzConfirmationDialogBoxButtonScheme } from '@@/library/v4/components/OzConfirmationDialogBox.vue'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { VerificationCodePurpose, type JsonApiData, type JsonApiResponse } from '@@/types'
import { fetchJson, fetchResponse, HTTPMethod } from '@padlet/fetch'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface SignupResponse {
  loginUrl?: string
  redirectUri?: string
  name: string
}

const buildSignUpRequestPayload = (
  email: string,
  password: string,
  role: string,
  inviteLinkCode: string,
  referralCode: string | null,
  codeChallenge: string | null,
  codeChallengeMethod: string | null,
  pkceState: string | null,
  originAppRedirectUri: string | null,
  originAppClientId: string | null,
): Record<string, unknown> => {
  return {
    data: {
      type: 'User',
      attributes: {
        email,
        password,
        role,
        inviteLinkCode,
        referralCode,
        codeChallenge,
        codeChallengeMethod,
        state: pkceState,
        redirectUri: originAppRedirectUri,
        clientId: originAppClientId,
      },
    },
  }
}

export const useAuthSignupStore = defineStore('authSignupStore', () => {
  const globalAlertDialogStore = useGlobalAlertDialogStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  // State
  const username = ref('')
  const password = ref('')
  const email = ref('')
  const tenantName = ref('')
  const role = ref(UserRole.DEFAULT)
  const inviteLinkCode = ref('')
  const usernameValidationMessage = ref('')
  const passwordValidationMessage = ref('')
  const isUsernameVerified = ref(false)
  const isAwaitingServerResponse = ref(false)
  const codeChallenge = ref<string | null>(null)
  const originAppClientId = ref<string | null>(null)
  const pkceState = ref<string | null>(null)
  const originAppRedirectUri = ref<string | null>(null)
  const codeChallengeMethod = ref<string | null>(null)
  const isEmailVerified = ref(false)
  const isEmailValidatedForSignup = ref(false)
  const isEmailVerifiedWithOtp = ref(false)
  const enforceEmailVerification = ref(true)
  const verificationCode = ref('')
  const verificationPurpose = ref<VerificationCodePurpose>(VerificationCodePurpose.Signup)
  const emailValidationMessage = ref('')
  const verificationCodeValidationMessage = ref('')
  const maximumPasswordLength = ref(MAX_PASSWORD_LENGTH)
  const minimumPasswordLength = ref(MIN_PASSWORD_LENGTH)
  const verifyMethods = ref([])
  const loginMethods = ref<LoginMethod[]>([])
  const userLoginMethods = ref<LoginMethod[]>([])
  const humanVerificationToken = ref<string | null>(null)

  // Getters
  const thirdPartyAuthLoginMethods = computed(
    (): Record<string, LoginMethod> => normalizeThirdPartyLoginMethods(loginMethods.value),
  )
  const thirdPartyAuthVerifyMethods = computed(() => normalizeThirdPartyLoginMethods(verifyMethods.value))
  const userThirdPartyAuthLoginMethods = computed(
    (): Record<string, LoginMethod> => normalizeThirdPartyLoginMethods(userLoginMethods.value),
  )
  const isEmailValid = computed(() => (emailValidationMessage.value.length > 0 ? 'no' : 'yes'))
  const isUsernameValid = computed(() => (usernameValidationMessage.value.length > 0 ? 'no' : 'yes'))
  const isPasswordValid = computed(() => (passwordValidationMessage.value.length > 0 ? 'no' : 'yes'))
  const isVerificationCodeValid = computed(() => (verificationCodeValidationMessage.value.length > 0 ? 'no' : 'yes'))
  const isPkceEnabled = computed(
    (): boolean => codeChallenge.value !== null && codeChallengeMethod.value !== null && pkceState.value !== null,
  )
  const isPasswordSignupEnabled = computed(() => userLoginMethods.value.some((method) => method.name === 'password'))

  // Actions

  async function initialize(startingState: {
    email?: string
    emailValidationMessage?: string
    loginMethods?: LoginMethod[]
    userLoginMethods?: LoginMethod[]
    originAppClientId?: string | null
    pkceState?: string | null
    codeChallenge?: string | null
    codeChallengeMethod?: string | null
    originAppRedirectUri?: string
    maximumPasswordLength?: number
    minimumPasswordLength?: number
    inviteLinkCode?: string
    role?: UserRole
    tenantName?: string
    errorCode?: string
    erroredSsoService?: string
    enforceEmailVerification?: boolean
  }): Promise<void> {
    if (startingState.loginMethods != null) {
      loginMethods.value = startingState.loginMethods
    }

    if (startingState.userLoginMethods != null) {
      userLoginMethods.value = startingState.userLoginMethods
    }

    if (startingState.originAppClientId != null) {
      originAppClientId.value = startingState.originAppClientId
    }

    if (startingState.pkceState != null) {
      pkceState.value = startingState.pkceState
    }

    if (startingState.codeChallenge != null) {
      codeChallenge.value = startingState.codeChallenge
    }

    if (startingState.codeChallengeMethod != null) {
      codeChallengeMethod.value = startingState.codeChallengeMethod
    }

    if (startingState.originAppRedirectUri != null) {
      originAppRedirectUri.value = startingState.originAppRedirectUri
    }

    if (startingState.maximumPasswordLength != null) {
      maximumPasswordLength.value = startingState.maximumPasswordLength
    }

    if (startingState.minimumPasswordLength != null) {
      minimumPasswordLength.value = startingState.minimumPasswordLength
    }

    if (startingState.inviteLinkCode != null) {
      inviteLinkCode.value = startingState.inviteLinkCode
    }

    if (startingState?.email != null) {
      email.value = startingState.email
      if (startingState.emailValidationMessage === '') {
        isEmailVerified.value = true
      }
    }

    if (startingState.tenantName != null) {
      tenantName.value = startingState.tenantName
    }

    if (startingState.errorCode != null && startingState.erroredSsoService != null) {
      clearQueryParamsWithoutReloading()
      openThirdPartyAuthErrorSnackbar({
        errorCode: startingState.errorCode,
        erroredSsoService: startingState.erroredSsoService,
      })
    } else if (startingState.errorCode != null) {
      clearQueryParamsWithoutReloading()
      // handle more generic errors not specific to a third party auth service
      openErrorSnackbar({
        errorCode: startingState?.errorCode,
      })
    }

    if (startingState.enforceEmailVerification != null) {
      enforceEmailVerification.value = startingState.enforceEmailVerification
    }
  }

  function openThirdPartyAuthErrorSnackbar({
    errorCode,
    erroredSsoService,
  }: {
    errorCode: string
    erroredSsoService: string
  }): void {
    if (errorCode === 'SSO_INVALID_INFO') {
      globalSnackbarStore.setSnackbar({
        message: p__(
          'Error message when user fails to login with Single Sign On (SSO), e.g. Google, Microsoft, and Apple because the email is invalid',
          'Sorry, your %{erroredSsoService} account email is most likely invalid. Please contact your administrator to create an account for you.',
          { erroredSsoService },
        ),
        notificationType: SnackbarNotificationType.error,
      })
    } else if (errorCode === 'SSO_AUTO_LINKING_DISALLOWED') {
      globalSnackbarStore.setSnackbar({
        message: p__(
          'Error message displayed when the user fails to log in with their Single Sign On (SSO) accounts - e.g. Login with Google, Microsoft, or Apple, because we do not allow auto linking of accounts, due to lack of verification or mismatch in account details',
          'Sorry, we could not log you in using %{erroredSsoService}. Please log in using your email and password instead.',
          { erroredSsoService },
        ),
        notificationType: SnackbarNotificationType.error,
      })
    } else if (errorCode === 'SSO_OPTION_DISALLOWED') {
      globalSnackbarStore.setSnackbar({
        message: p__(
          'Error message displayed when the user fails to sign up with their Single Sign On (SSO) accounts - e.g. Login with Google, Microsoft, or Apple, because the option is disallowed',
          'Your school does not allow signing up with %{erroredSsoService}. Please use one of the approved sign up methods below.',
          { erroredSsoService },
        ),
        notificationType: SnackbarNotificationType.error,
      })
    } else {
      globalSnackbarStore.genericFetchError()
    }
  }

  function openErrorSnackbar({ errorCode }: { errorCode: string }): void {
    if (errorCode === 'LOGIN_TOKEN_EXPIRED' || errorCode === 'SELECT_ACCOUNT_TOKEN_EXPIRED') {
      globalSnackbarStore.setSnackbar({
        message: __('Login token expired'),
        notificationType: SnackbarNotificationType.error,
        persist: true,
      })
    } else if (errorCode === 'NO_USER') {
      globalSnackbarStore.setSnackbar({
        message: p__(
          'Error message when a user is not found for given email or username',
          'Sorry, we could not find a user with the email or username you provided. Please try again.',
        ),
        notificationType: SnackbarNotificationType.error,
      })
    } else {
      // this error is triggered when user exceeds rate limit for verification attempts
      // but we display a generic error and use a non-descriptive error code in the url query param
      globalSnackbarStore.genericFetchError()
    }
  }

  function setEmail(newEmail: string): void {
    email.value = newEmail
  }
  function setVerificationCode(newVerificationCode: string): void {
    verificationCode.value = newVerificationCode
  }

  function setPassword(newPassword: string): void {
    password.value = newPassword
  }

  function setEmailValidationMessage(message: string): void {
    emailValidationMessage.value = message
  }
  function setVerificationCodeValidationMessage(message: string): void {
    verificationCodeValidationMessage.value = message
  }

  function setPasswordValidationMessage(message: string): void {
    passwordValidationMessage.value = message
  }

  function setIsEmailVerified(value: boolean): void {
    isEmailVerified.value = value
  }

  function setIsEmailValidatedForSignup(value: boolean): void {
    isEmailValidatedForSignup.value = value
  }

  function setIsEmailVerifiedWithOtp(value: boolean): void {
    isEmailVerifiedWithOtp.value = value
  }

  function setUsernameValidationMessage(message: string): void {
    usernameValidationMessage.value = message
  }

  function setIsUsernameVerified(value: boolean): void {
    isUsernameVerified.value = value
  }

  async function completeSignupWithPassword(): Promise<void> {
    try {
      if (email.value === '' || password.value === '') return

      if (password.value.length < minimumPasswordLength.value) {
        passwordValidationMessage.value = __('Password is too short')
        return
      }

      if (password.value.length > maximumPasswordLength.value) {
        passwordValidationMessage.value = __('Password is too long')
        return
      }

      isAwaitingServerResponse.value = true
      const signUpUrl = `/api/auth/signup/users`

      const referralCode = getSearchParams().referral_code ?? getCookie('ww_arc')
      const response: JsonApiResponse<SignupResponse> = await fetchJson(signUpUrl, {
        method: HTTPMethod.post,
        csrfToken: getCsrfToken(),
        jsonData: buildSignUpRequestPayload(
          email.value,
          password.value,
          role.value,
          inviteLinkCode.value,
          referralCode,
          codeChallenge.value,
          codeChallengeMethod.value,
          pkceState.value,
          originAppRedirectUri.value,
          originAppClientId.value,
        ),
      })
      if (!isPkceEnabled.value) {
        const loginUrl = (response.data as JsonApiData<SignupResponse>)?.attributes?.loginUrl as string
        navigateTo(loginUrl)
      } else {
        const redirectUri = (response.data as JsonApiData<SignupResponse>)?.attributes?.redirectUri as string
        navigateTo(redirectUri)
      }
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        try {
          const parsedErrorMessage = JSON.parse(e.message)
          const error = parsedErrorMessage.errors[0]

          if (error.code === ApiErrorCode.INVALID_EMAIL) {
            emailValidationMessage.value = __('Email is invalid')
          } else if (error.code === ApiErrorCode.EMAIL_ALREADY_IN_USE) {
            emailValidationMessage.value = __('Email is taken')
            promptLogin()
          }
        } catch (jsonException) {
          captureException(jsonException)
        }
      } else {
        captureFetchException(e, { source: 'AuthSignupCompleteSignupWithPassword' })
      }
      openCreateAccountErrorDialog()
      isAwaitingServerResponse.value = false
    }
  }

  async function continueSignupWithEmail(): Promise<void> {
    try {
      if (email.value === '') return

      if (!isEmail(email.value)) {
        isEmailVerified.value = false
        emailValidationMessage.value = __('Email is invalid')
        return
      }
      isAwaitingServerResponse.value = true
      const url = transformCurrentUrl(
        { withQueryString: true },
        {
          path: '/api/auth/signup/check_email',
        },
      )
      const res = await fetchResponse(url, {
        method: HTTPMethod.post,
        body: asciiSafeStringify({
          email: email.value,
          human_verification_token: humanVerificationToken.value,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
      })
      try {
        const jsonResponse = await res.json()
        if (jsonResponse?.data?.attributes?.emailVerificationRequired === true) {
          enforceEmailVerification.value = true
        } else {
          enforceEmailVerification.value = false
        }
        if (jsonResponse?.data?.attributes?.verifyMethods != null) {
          verifyMethods.value = jsonResponse.data.attributes.verifyMethods
        } else {
          // if verifyMethods is not returned, we should clear the verifyMethods state
          // since the user might go back and forth between screens and change to an email that does not have applicable SSO verify methods
          verifyMethods.value = []
        }

        if (jsonResponse?.data?.attributes?.userLoginMethods != null) {
          userLoginMethods.value = jsonResponse.data.attributes.userLoginMethods
        } else {
          userLoginMethods.value = []
        }
      } catch {
        // do nothing
      }
      isEmailVerified.value = true
      isEmailValidatedForSignup.value = true
    } catch (e) {
      isEmailVerified.value = false

      if (e.status === HttpCode.Forbidden) {
        // Turnstile verification failed
        emailValidationMessage.value = __('Verification failed. Please try again.')
        return
      } else if (e.status === HttpCode.UnprocessableEntity) {
        try {
          const parsedErrorMessage = JSON.parse(e.message)
          const error = parsedErrorMessage.errors[0]

          if (error.code === ApiErrorCode.INVALID_EMAIL) {
            emailValidationMessage.value = __('Email is invalid')
            return
          } else if (error.code === ApiErrorCode.EMAIL_ALREADY_IN_USE) {
            emailValidationMessage.value = __('Email is taken')
            promptLogin()
            return
          }
        } catch (jsonException) {
          captureException(jsonException)
        }
      } else {
        captureFetchException(e, { source: 'AuthSignupContinueSignupWithEmail' })
      }

      openCreateAccountErrorDialog()
    } finally {
      isAwaitingServerResponse.value = false
      // Clear the token so a stale (already-consumed) token is never re-sent.
      // The Turnstile widget will be reset by the caller to obtain a fresh one.
      humanVerificationToken.value = null
    }
  }

  async function continueWithOtpVerification(): Promise<void> {
    if (email.value === '' || verificationCode.value === '') return
    try {
      isAwaitingServerResponse.value = true
      await EmailAddressVerificationApi.verifyEmailAddress(
        email.value,
        verificationCode.value,
        verificationPurpose.value,
      )
      isEmailVerifiedWithOtp.value = true
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        try {
          const parsedErrorMessage = JSON.parse(e.message)
          const errorCode = parsedErrorMessage?.errors?.[0]?.code
          if (errorCode === 'UNSUPPORTED_DOMAIN') {
            verificationCodeValidationMessage.value = __('Email address verification is not supported for your domain')
          } else if (errorCode === 'INCORRECT_VERIFICATION_CODE') {
            verificationCodeValidationMessage.value = __('Incorrect verification code')
          }
        } catch (jsonException) {
          captureException(jsonException)
        }
      } else if (e.status === HttpCode.RateLimitError) {
        globalSnackbarStore.setSnackbar({
          message: __('You’ve reached the limit for verification attempts. Please try again in one hour.'),
          notificationType: SnackbarNotificationType.error,
        })
      } else {
        globalSnackbarStore.setSnackbar({
          message: __('Unable to verify'),
          notificationType: SnackbarNotificationType.error,
        })
        captureFetchException(e, { source: 'AuthSignupContinueWithOtpVerification' })
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  async function resendVerificationCode(): Promise<void> {
    if (email.value === '') return
    try {
      isAwaitingServerResponse.value = true
      await EmailAddressVerificationApi.resendVerificationCode(email.value, verificationPurpose.value)
      globalSnackbarStore.setSnackbar({
        message: __('Email sent'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      if (e.status === HttpCode.RateLimitError) {
        globalSnackbarStore.setSnackbar({
          message: __('Too many emails sent. Please try again later.'),
          notificationType: SnackbarNotificationType.error,
        })
      } else {
        globalSnackbarStore.genericFetchError()
        captureFetchException(e, { source: 'AuthSignupResendVerificationCode' })
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  function openCreateAccountErrorDialog(): void {
    globalAlertDialogStore.openAlertDialog({
      ...FACE_WITH_ROLLING_EYES_EMOJI,
      title: __('Unable to create account'),
      body: __('There was an issue with your request. Please try again later!'),
      closeButtonText: __('Okay'),
    })
  }

  function promptLogin(): void {
    globalConfirmationDialogStore.openConfirmationDialog({
      ...WAVING_HAND_EMOJI,
      title: __('Welcome back'),
      body: __('Looks like you already have an account with %{tenantName}. Would you like to log in instead?', {
        tenantName: tenantName.value,
      }),
      confirmButtonText: __('Continue to log in'),
      cancelButtonText: __('Nevermind'),
      afterConfirmActions: [redirectToLogin],
      forceFullWidthButtons: true,
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Default,
    })
  }

  function redirectToLogin(): void {
    const url = transformCurrentUrl(
      { withQueryString: true },
      {
        path: '/auth/login',
        searchParams: { email: email.value },
      },
    )
    navigateTo(url)
  }

  return {
    // State

    // Getters
    username,
    password,
    usernameValidationMessage,
    passwordValidationMessage,
    isAwaitingServerResponse,
    thirdPartyAuthLoginMethods,
    userThirdPartyAuthLoginMethods,
    isUsernameVerified,
    isUsernameValid,
    isPasswordValid,
    codeChallenge,
    codeChallengeMethod,
    pkceState,
    originAppClientId,
    originAppRedirectUri,
    isPkceEnabled,
    email,
    isEmailVerified,
    isEmailValidatedForSignup,
    isEmailVerifiedWithOtp,
    enforceEmailVerification,
    verificationCode,
    emailValidationMessage,
    verificationCodeValidationMessage,
    minimumPasswordLength,
    isEmailValid,
    isVerificationCodeValid,
    thirdPartyAuthVerifyMethods,
    isPasswordSignupEnabled,

    humanVerificationToken,

    // Actions
    initialize,
    setPassword,
    setUsernameValidationMessage,
    setPasswordValidationMessage,
    setIsUsernameVerified,
    setEmail,
    setVerificationCode,
    setEmailValidationMessage,
    setVerificationCodeValidationMessage,
    setIsEmailVerified,
    setIsEmailValidatedForSignup,
    setIsEmailVerifiedWithOtp,
    completeSignupWithPassword,
    continueSignupWithEmail,
    continueWithOtpVerification,
    resendVerificationCode,
  }
})
